import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Typography, Upload, Button, Progress, App } from 'antd';
import { PrimaryButton } from '../../Components/Buttons/Buttons';
import { InboxOutlined, FileExcelOutlined, FileTextOutlined, DeleteOutlined } from '@ant-design/icons';
import { primaryShades } from '../../../theme/theme';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';

const { Paragraph } = Typography;
const { Dragger } = Upload;

/**
 * Step component for uploading a CSV or Excel file
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.uploadProps - Properties for the upload component
 * @param {boolean} props.parsingFile - Whether the file is being parsed
 * @param {Function} props.onFileProcessed - Callback when file is processed successfully
 * @param {Function} props.onFileUploaded - Callback when file is uploaded
 */
const UploadFileStep = forwardRef(({ uploadProps, parsingFile, onFileProcessed = () => { }, onFileUploaded = () => { } }, ref) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [fileProcessed, setFileProcessed] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const [progressStatus, setProgressStatus] = useState('');

    const { message } = App.useApp();

    // Esponi il metodo processFile tramite ref
    useImperativeHandle(ref, () => ({
        processFile
    }));

    // Funzione per ottenere l'icona corretta in base al tipo di file
    const getFileIcon = (file) => {
        if (file.type === 'text/csv') {
            return <FileTextOutlined style={{ fontSize: '1rem', color: primaryShades.primary500 }} />;
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel') {
            return <FileExcelOutlined style={{ fontSize: '1rem', color: primaryShades.primary500 }} />;
        }
        return <FileTextOutlined style={{ fontSize: '1rem', color: primaryShades.primary500 }} />;
    };

    // Funzione per formattare la dimensione del file
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Modifica dell'oggetto uploadProps per tracciare quando un file è stato caricato
    const modifiedUploadProps = {
        ...uploadProps,
        beforeUpload: (file) => {
            // Chiamiamo il beforeUpload originale e salviamo il risultato
            const result = uploadProps.beforeUpload ? uploadProps.beforeUpload(file) : false;

            // Reset dello stato di elaborazione quando viene caricato un nuovo file
            setFileProcessed(false);
            setProgressPercent(0);
            setProgressStatus('');

            // Notifica il padre che un file è stato caricato
            onFileUploaded(file);

            return result;
        },
        onRemove: () => {
            setFileProcessed(false);
            setProgressPercent(0);
            setProgressStatus('');
            if (uploadProps.onRemove) {
                uploadProps.onRemove();
            }
        }
    };

    // Funzione per estrarre i dati dal file CSV
    const parseCSV = (file) => {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    resolve(results.data);
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    reject(error);
                }
            });
        });
    };

    // Funzione per estrarre i dati dal file Excel
    const parseExcel = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target.result;
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const json = XLSX.utils.sheet_to_json(worksheet);
                    resolve(json);
                } catch (error) {
                    console.error('Error parsing Excel:', error);
                    reject(error);
                }
            };
            reader.onerror = (error) => {
                console.error('Error reading file:', error);
                reject(error);
            };
            reader.readAsArrayBuffer(file);
        });
    };

    // Funzione per normalizzare i nomi delle colonne
    const normalizeColumnNames = (data) => {
        return data.map(row => {
            const normalizedRow = {};

            // Itera su tutte le chiavi dell'oggetto
            Object.keys(row).forEach(key => {
                // Normalizza il nome della chiave (rimuovi spazi, converti in minuscolo)
                const normalizedKey = key.trim().toLowerCase();

                // Mappa i nomi delle colonne ai nomi standard
                if (normalizedKey.includes('sku') || normalizedKey === 'codice' || normalizedKey === 'code') {
                    normalizedRow.sku = row[key];
                } else if (normalizedKey.includes('ean')) {
                    normalizedRow.ean = row[key];
                } else if (normalizedKey.includes('upc')) {
                    normalizedRow.upc = row[key];
                } else {
                    // Mantieni la chiave originale se non corrisponde a nessuna delle precedenti
                    normalizedRow[normalizedKey] = row[key];
                }
            });

            return normalizedRow;
        });
    };

    // Funzione per creare l'array di oggetti nel formato richiesto
    const createCodesArray = (data) => {
        return data.map(row => {
            const codeObj = {};

            // Aggiungi SKU se presente
            if (row.sku && row.sku.toString().trim() !== '') {
                codeObj.skuCode = row.sku.toString().trim();
            }

            // Aggiungi EAN se presente
            if (row.ean && row.ean.toString().trim() !== '') {
                codeObj.eanCode = row.ean.toString().trim();
            }

            // Aggiungi UPC se presente
            if (row.upc && row.upc.toString().trim() !== '') {
                codeObj.upcCode = row.upc.toString().trim();
            }

            return codeObj;
        }).filter(obj => Object.keys(obj).length > 0); // Rimuovi oggetti vuoti
    };

    // Funzione per processare il file tramite la Firebase function
    const processFile = async () => {
        // Verifichiamo che ci sia un file da processare
        if (uploadProps.fileList.length === 0) {
            console.error('No file to process');
            message.error('No file to process');
            return;
        }

        setIsProcessing(true);
        setProgressPercent(0);
        setProgressStatus('Initialising...');
        const file = uploadProps.fileList[0];

        try {
            // Verifichiamo che l'URL della Firebase function sia definito
            const firebaseFunctionUrl = process.env.REACT_APP_ANALYSIS_FILE;
            if (!firebaseFunctionUrl) {
                throw new Error('Firebase function URL not defined. Check the REACT_APP_ANALYSIS_FILE environment variable.');
            }

            setProgressStatus('Reading file...');

            // Estrai i dati dal file in base al tipo
            let fileData;
            if (file.type === 'text/csv') {
                fileData = await parseCSV(file);
            } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel') {
                fileData = await parseExcel(file);
            } else {
                throw new Error('Unsupported file format. Please use CSV or Excel.');
            }

            setProgressStatus('Processing data...');

            // Normalizza i nomi delle colonne
            const normalizedData = normalizeColumnNames(fileData);

            // Crea l'array di oggetti nel formato richiesto
            const codesArray = createCodesArray(normalizedData);

            // Verifica che ci siano dati validi
            if (codesArray.length === 0) {
                throw new Error('No valid codes found in the file. Please ensure the file contains SKU, EAN or UPC columns.');
            }

            // Verifica il limite di 250 righe
            if (codesArray.length > 250) {
                throw new Error(`File contains ${codesArray.length} rows. Maximum allowed is 250 rows. Please reduce the number of items in your file.`);
            }

            setProgressStatus('Sending data to server...');

            // Chiamata alla Firebase function con gestione dello streaming usando fetch
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 secondi timeout

            let response;
            try {
                response = await fetch(firebaseFunctionUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(codesArray),
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    throw new Error('Request timed out after 60 seconds');
                }
                throw fetchError;
            }

            // Progress will be updated by server responses

            // Leggi la risposta come stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let latestResponse = null;

            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    // Decodifica il chunk ricevuto
                    const chunk = decoder.decode(value, { stream: true });
                    buffer += chunk;

                    // Processa tutte le righe complete nel buffer
                    let lines = buffer.split('\n');
                    // Mantieni l'ultima riga nel buffer se non è completa
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                const parsedLine = JSON.parse(line.trim());

                                // Aggiorna la progress bar se c'è un campo progress
                                if (parsedLine.hasOwnProperty('progress')) {
                                    const serverProgress = parsedLine.progress;

                                    // Usa direttamente il progresso dal server
                                    setProgressPercent(serverProgress);

                                    // Aggiorna lo status in base al progresso
                                    if (parsedLine.totalItems && parsedLine.processedItems !== undefined) {
                                        setProgressStatus(`Processing items: ${parsedLine.processedItems}/${parsedLine.totalItems}`);
                                    } else if (serverProgress < 100) {
                                        setProgressStatus(`Processing: ${serverProgress}%`);
                                    } else {
                                        setProgressStatus('Finalizing...');
                                    }
                                }

                                // Salva l'ultima risposta valida
                                latestResponse = parsedLine;
                            } catch (parseError) {
                                console.warn('Failed to parse line as JSON:', line, parseError);
                            }
                        }
                    }
                }

                // Processa l'ultima riga rimasta nel buffer
                if (buffer.trim()) {
                    try {
                        const parsedLine = JSON.parse(buffer.trim());

                        // Aggiorna la progress bar anche per l'ultima riga
                        if (parsedLine.hasOwnProperty('progress')) {
                            const serverProgress = parsedLine.progress;

                            // Usa direttamente il progresso dal server
                            setProgressPercent(serverProgress);

                            if (parsedLine.totalItems && parsedLine.processedItems !== undefined) {
                                setProgressStatus(`Processing items: ${parsedLine.processedItems}/${parsedLine.totalItems}`);
                            } else if (serverProgress < 100) {
                                setProgressStatus(`Processing: ${serverProgress}%`);
                            } else {
                                setProgressStatus('Finalizing...');
                            }
                        }

                        latestResponse = parsedLine;
                    } catch (parseError) {
                        console.warn('Failed to parse final line as JSON:', buffer, parseError);
                    }
                }
            } finally {
                reader.releaseLock();
            }

            // Usa l'ultima risposta valida per determinare il risultato finale
            if (latestResponse) {
                // Verifica se il processo è completato con successo
                if (latestResponse.progress === 100 && latestResponse.results) {
                    setProgressPercent(100);
                    setProgressStatus('Complete');
                    setFileProcessed(true);
                    onFileProcessed(true, latestResponse);
                    // Messaggio di successo gestito dal parent component
                } else if (latestResponse.results && latestResponse.results.length > 0) {
                    // Anche se non c'è il campo progress, se ci sono risultati consideriamo il processo completato
                    setProgressPercent(100);
                    setProgressStatus('Complete');
                    setFileProcessed(true);
                    onFileProcessed(true, latestResponse);
                    // Messaggio di successo gestito dal parent component
                } else {
                    onFileProcessed(false, latestResponse);
                    message.warning('No valid results received from server.');
                }
            } else {
                throw new Error('No valid response received from server');
            }

        } catch (error) {
            console.error('Error processing file:', error);
            setProgressPercent(0);
            setProgressStatus('Error');

            // Mostriamo informazioni dettagliate sull'errore
            if (error.response) {
                // La richiesta è stata effettuata e il server ha risposto con un codice di stato
                // che non rientra nell'intervallo 2xx
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response headers:', error.response.headers);
                message.error(`Server error: ${error.response.status} - ${JSON.stringify(error.response.data)}`);
            } else if (error.request) {
                // La richiesta è stata effettuata ma non è stata ricevuta alcuna risposta
                console.error('No response received:', error.request);
                message.error('No response received from server. Please check your connection.');
            } else {
                // Si è verificato un errore durante l'impostazione della richiesta
                // Per errori di validazione (come il limite di righe), mostriamo solo il messaggio user-friendly
                message.error(error.message);
            }

            onFileProcessed(false, { error: error.message });
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem 0',
            gap: '0.5rem'
        }}>
            {/* Header section */}
            <div style={{ flexShrink: 0 }}>
                <Paragraph style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                    Upload a CSV or Excel file containing the SKUs of the products you want to order.
                    The file must contain at least one column with the product SKUs, EANs, or UPCs.
                </Paragraph>

                <Paragraph style={{
                    color: '#faad14',
                    fontWeight: 'bold',
                    marginBottom: '1rem',
                    fontSize: '0.875rem'
                }}>
                    ⚠️ Maximum 250 rows allowed per file. Files with more than 250 items will be rejected.
                </Paragraph>
            </div>

            {/* Download buttons section */}
            <div style={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '0.5rem'
            }}>
                <Paragraph strong style={{ margin: 0, fontSize: '0.875rem' }}>
                    Download an example file to see the required format:
                </Paragraph>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <a href={require('../../../assets/file/example_csv.csv')} download>
                        <PrimaryButton type="primary">Download Example CSV</PrimaryButton>
                    </a>
                    <a href={require('../../../assets/file/example_excel.xlsx')} download>
                        <PrimaryButton type="primary">Download Example XLSX</PrimaryButton>
                    </a>
                </div>
            </div>

            {/* Drag & Drop area */}
            <div style={{ flexShrink: 0 }}>
                <Dragger
                    {...modifiedUploadProps}
                    style={{
                        padding: '1rem',
                        minHeight: '8rem',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                    showUploadList={false}
                >
                    <p className="ant-upload-drag-icon" style={{ fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text" style={{ margin: '0 0 0.25rem 0' }}>Click or drag a file to this area to upload</p>
                    <p className="ant-upload-hint" style={{ margin: '0 0 0.5rem 0' }}>
                        Supports single files in CSV or Excel format. Maximum 250 rows allowed.
                    </p>

                    {/* Mostra il file caricato direttamente nell'area drag & drop */}
                    {uploadProps.fileList && uploadProps.fileList.length > 0 && (
                        <div style={{
                            marginTop: '0.75rem',
                            padding: '0.75rem',
                            backgroundColor: `${primaryShades.primary100}`,
                            border: `1px solid ${primaryShades.primary300}`,
                            borderRadius: '0.375rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            minWidth: '12rem',
                            width: 'fit-content',
                            margin: '0.75rem auto 0'
                        }}>
                            <div style={{ flexShrink: 0 }}>
                                {getFileIcon(uploadProps.fileList[0])}
                            </div>
                            <div style={{ flex: 1, textAlign: 'left' }}>
                                <div style={{
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    color: primaryShades.primary700,
                                    marginBottom: '0.125rem'
                                }}>
                                    {uploadProps.fileList[0].name}
                                </div>
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: primaryShades.primary600
                                }}>
                                    {formatFileSize(uploadProps.fileList[0].size)}
                                </div>
                            </div>
                            <Button
                                type="text"
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (modifiedUploadProps.onRemove) {
                                        modifiedUploadProps.onRemove();
                                    }
                                }}
                                style={{
                                    color: primaryShades.primary500,
                                    flexShrink: 0
                                }}
                                title="Remove file"
                            />
                        </div>
                    )}
                </Dragger>
            </div>

            {/* Progress Bar - fixed at bottom */}
            {(isProcessing || progressPercent > 0) && (
                <div style={{
                    flexShrink: 0,
                    paddingBottom: '0.25rem'
                }}>
                    <Progress
                        percent={progressPercent}
                        status={progressPercent === 100 ? 'success' : (progressStatus === 'Error' ? 'exception' : 'active')}
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                    />
                    {progressStatus && (
                        <div style={{
                            textAlign: 'center',
                            marginTop: '0.25rem',
                            fontSize: '0.75rem',
                            color: '#666',
                            lineHeight: '1.2'
                        }}>
                            {progressStatus}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default UploadFileStep; 